import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  slug: "",
  common_name: "",
  scientific_name: "",
  categories: [],
  image_url: "",
  summary: "",
  identification: "",
  parts_used: "",
  preparation: "",
  dosage: "",
  benefits: "",
  cautions: "",
  lookalikes: "",
};

const CATS = ["weight_loss", "detox", "cognitive", "general_wellness"];

export default function Admin() {
  const [plants, setPlants] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/plants");
      setPlants(data);
    } catch {
      setPlants([]);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setCreating(false);
    setEditing(p.slug);
    setForm({ ...EMPTY, ...p });
  };

  const startCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm(EMPTY);
  };

  const cancel = () => {
    setCreating(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const toggleCat = (c) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
    }));
  };

  const save = async () => {
    try {
      if (creating) {
        await api.post("/admin/plants", form);
        toast.success("Plant created");
      } else if (editing) {
        await api.put(`/admin/plants/${editing}`, form);
        toast.success("Plant updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    try {
      await api.delete(`/admin/plants/${slug}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const showForm = creating || editing;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-14">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-eyebrow">Control room</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">Plant management</h1>
        </div>
        <button data-testid="admin-new-btn" onClick={startCreate} className="btn-primary">
          <Plus className="w-4 h-4" /> New plant
        </button>
      </div>

      {showForm && (
        <div className="card-journal mt-10" data-testid="admin-form">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl">{creating ? "New plant" : `Edit · ${editing}`}</h2>
            <button onClick={cancel} className="btn-ghost"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-5 mt-6">
            {[
              ["slug", "Slug (kebab-case, unique)"],
              ["common_name", "Common name"],
              ["scientific_name", "Scientific name"],
              ["image_url", "Image URL"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="label-eyebrow">{label}</label>
                <input data-testid={`admin-field-${k}`} disabled={!creating && k === "slug"} className="field mt-2" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <div className="mt-5">
            <p className="label-eyebrow">Categories</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {CATS.map((c) => (
                <button
                  key={c}
                  data-testid={`admin-cat-${c}`}
                  type="button"
                  onClick={() => toggleCat(c)}
                  className={`chip cursor-pointer ${form.categories.includes(c) ? "bg-[var(--forest)] text-[var(--paper)] border-[var(--forest)]" : ""}`}
                >
                  {c.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          {["summary", "identification", "parts_used", "preparation", "dosage", "benefits", "cautions", "lookalikes"].map((k) => (
            <div key={k} className="mt-5">
              <label className="label-eyebrow">{k.replace("_", " ")}</label>
              <textarea data-testid={`admin-field-${k}`} className="field mt-2 resize-y min-h-[80px]" rows={3} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div className="mt-7 flex gap-3">
            <button data-testid="admin-save-btn" onClick={save} className="btn-primary"><Save className="w-4 h-4" /> Save</button>
            <button onClick={cancel} className="btn-outline">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-12 border-t border-[var(--border-line)]">
        {plants.map((p) => (
          <div key={p.slug} className="flex items-center gap-4 py-4 border-b border-[var(--border-line)]" data-testid={`admin-row-${p.slug}`}>
            <img src={p.image_url} alt="" className="w-14 h-14 object-cover frame-border p-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg">{p.common_name}</p>
              <p className="font-mono text-xs italic text-[var(--sage)]">{p.scientific_name}</p>
            </div>
            <p className="hidden md:block text-xs text-[var(--ink-soft)]">{(p.categories || []).join(", ")}</p>
            <button data-testid={`admin-edit-${p.slug}`} onClick={() => startEdit(p)} className="btn-ghost text-sm">Edit</button>
            <button data-testid={`admin-delete-${p.slug}`} onClick={() => remove(p.slug)} className="btn-ghost text-[var(--terracotta)]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
